import React, { useState } from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { toast } from "@/components/ui/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { useActivityLogger } from '@/hooks/useActivityLogger';
import { useFileUpload } from '@/hooks/useFileUpload';
import { useQuotePaymentGenerator } from '@/hooks/useQuotePaymentGenerator';
import { Loader2, Info } from 'lucide-react';
import QuoteTypeSelector from './quote-form/QuoteTypeSelector';
import VariationReasonField from './quote-form/VariationReasonField';
import QuoteAmountField from './quote-form/QuoteAmountField';
import QuoteNotesField from './quote-form/QuoteNotesField';
import QuoteFileUploadField from './quote-form/QuoteFileUploadField';
import QuoteLinkField from './quote-form/QuoteLinkField';
import PaymentLinkField from './quote-form/PaymentLinkField';
import PaymentOptionsToggle from './quote-form/PaymentOptionsToggle';
import QuoteIdentifierField from './quote-form/QuoteIdentifierField';
import { usePaymentFlowConfig } from '@/hooks/usePaymentFlowConfig';
import { Alert, AlertDescription } from '@/components/ui/alert';

const quoteFormSchema = z.object({
  quotedAmount: z.string().refine(value => {
    return /^\d*\.?\d*$/.test(value);
  }, {
    message: "Please enter a valid number.",
  }).refine(value => {
    return /\d/.test(value);
  }, {
    message: "Please enter a valid number.",
  }),
  notes: z.string().optional(),
  quoteType: z.enum(['initial', 'revised', 'variation']).default('initial'),
  variationReason: z.string().optional(),
  quoteLink: z.string().url().optional().or(z.literal("")),
  paymentOption: z.enum(['none', 'manual', 'generate']).default('none'),
  paymentUrl: z.string().url().optional().or(z.literal("")),
  externalIdentifier: z.string().optional(),
}).refine((data) => {
  if (data.quoteType === 'variation' && !data.variationReason) {
    return false;
  }
  return true;
}, {
  message: "Variation reason is required for variation quotes",
  path: ["variationReason"],
}).refine((data) => {
  if (data.paymentOption === 'manual' && !data.paymentUrl) {
    return false;
  }
  return true;
}, {
  message: "Payment URL is required when manual payment link is selected",
  path: ["paymentUrl"],
});

interface QuoteFormProps {
  bookingId: string;
  onQuoteAdded: () => void;
  isAdmin?: boolean;
  isFirstQuote?: boolean;
  defaultValues?: {
    quoteType?: 'initial' | 'revised' | 'variation';
    parentQuoteId?: string;
    quotedAmount?: string;
    variationReason?: string;
  };
}

const QuoteForm: React.FC<QuoteFormProps> = ({ 
  bookingId, 
  onQuoteAdded, 
  isAdmin = false,
  isFirstQuote = false,
  defaultValues 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { logQuoteAdded } = useActivityLogger();
  const { uploading, uploadFile } = useFileUpload();
  const { generatePaymentLink } = useQuotePaymentGenerator([], () => {
    console.log('[QUOTE-FORM] Payment link generated callback');
    onQuoteAdded();
  });
  const { shouldSkipPayment, paymentMode } = usePaymentFlowConfig();

  const form = useForm<z.infer<typeof quoteFormSchema>>({
    resolver: zodResolver(quoteFormSchema),
    defaultValues: {
      quotedAmount: defaultValues?.quotedAmount || "",
      notes: "",
      quoteType: defaultValues?.quoteType || 'initial',
      variationReason: defaultValues?.variationReason || "",
      quoteLink: "",
      paymentOption: 'none',
      paymentUrl: "",
      externalIdentifier: "",
    },
  });

  const watchQuoteType = form.watch('quoteType');
  const watchPaymentOption = form.watch('paymentOption');

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
  };

  const handleSubmit = async (values: z.infer<typeof quoteFormSchema>) => {
    try {
      setIsSubmitting(true);
      
      let quoteFileUrl: string | null = null;

      // Upload file if selected
      if (selectedFile) {
        const fileName = `quote-${bookingId}-${Date.now()}-${selectedFile.name}`;
        quoteFileUrl = await uploadFile(selectedFile, 'quote-documents', fileName);
        
        if (!quoteFileUrl) {
          return;
        }
      }

      // Prepare payment URL based on payment option
      const paymentUrl = values.paymentOption === 'manual' ? values.paymentUrl || null : null;

      console.log('[QUOTE-FORM] Submitting quote with data:', {
        booking_id: bookingId,
        quoted_amount: parseFloat(values.quotedAmount),
        submission_notes: values.notes,
        status: 'pending',
        quote_file_url: quoteFileUrl,
        quote_link: values.quoteLink || null,
        quote_type: values.quoteType,
        parent_quote_id: defaultValues?.parentQuoteId || null,
        variation_reason: values.quoteType === 'variation' ? values.variationReason : null,
        payment_url: paymentUrl,
        payment_option: values.paymentOption,
        external_identifier: values.externalIdentifier || null
      });

      const { data, error } = await supabase
        .from('quotes')
        .insert([
          { 
            booking_id: bookingId,
            quoted_amount: parseFloat(values.quotedAmount),
            submission_notes: values.notes,
            status: 'pending',
            quote_file_url: quoteFileUrl,
            quote_link: values.quoteLink || null,
            quote_type: values.quoteType,
            parent_quote_id: defaultValues?.parentQuoteId || null,
            variation_reason: values.quoteType === 'variation' ? values.variationReason : null,
            payment_url: paymentUrl,
            external_identifier: values.externalIdentifier || null
          }
        ])
        .select()

      if (error) {
        console.error("Error creating quote:", error);
        toast({
          title: "Error",
          description: "Failed to add the quote. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      if (data && data[0]) {
        const createdQuote = data[0];
        
        // Log the activity with context about quote type
        const activityDescription = values.quoteType === 'initial' 
          ? 'Initial quote added'
          : values.quoteType === 'revised'
          ? 'Revised quote added'
          : 'Variation quote added';
          
        await logQuoteAdded(bookingId, 'Admin User', 'admin@system.com', parseFloat(values.quotedAmount));
        
        // Handle payment link generation if selected
        if (values.paymentOption === 'generate' && createdQuote.id) {
          console.log('[QUOTE-FORM] Generating payment link for quote:', createdQuote.id);
          try {
            await generatePaymentLink(createdQuote.id);
          } catch (error) {
            console.error('[QUOTE-FORM] Failed to generate payment link:', error);
            // Don't fail the entire operation - just log the error
          }
        }
        
        const paymentInfo = values.paymentOption === 'manual' 
          ? ' with payment link'
          : values.paymentOption === 'generate'
          ? ' with auto-generated payment link'
          : '';
        
        const toastDescription = values.quoteType === 'initial'
          ? `The quote has been successfully added${selectedFile ? ' with document' : ''}${values.quoteLink ? ' with link' : ''}${paymentInfo}.`
          : values.quoteType === 'revised'
          ? `The revised quote has been successfully added${selectedFile ? ' with document' : ''}${values.quoteLink ? ' with link' : ''}${paymentInfo}.`
          : `The variation quote has been successfully added${selectedFile ? ' with document' : ''}${values.quoteLink ? ' with link' : ''}${paymentInfo}.`;
        
        toast({
          title: "Quote added",
          description: toastDescription,
        });
        
        form.reset();
        setSelectedFile(null);
        onQuoteAdded();
      }
    } catch (error) {
      console.error("Error creating quote:", error);
      toast({
        title: "Error",
        description: "Failed to add the quote. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading = isSubmitting || uploading;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        {shouldSkipPayment && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Payment handled via {paymentMode === 'swift_direct' ? 'Swift Account' : 'configured payment gateway'}. 
              Quote acceptance will automatically book the shipment.
            </AlertDescription>
          </Alert>
        )}

        <QuoteTypeSelector
          control={form.control}
          isFirstQuote={isFirstQuote}
          isLoading={isLoading}
          defaultQuoteType={defaultValues?.quoteType}
          watchQuoteType={watchQuoteType}
        />

        <VariationReasonField
          control={form.control}
          watchQuoteType={watchQuoteType}
          isLoading={isLoading}
        />

        <QuoteAmountField
          control={form.control}
          isLoading={isLoading}
        />

        <QuoteNotesField
          control={form.control}
          isLoading={isLoading}
        />

        <QuoteFileUploadField
          selectedFile={selectedFile}
          onFileSelect={handleFileSelect}
          onFileRemove={handleFileRemove}
          isLoading={isLoading}
        />

        <QuoteLinkField
          control={form.control}
          isLoading={isLoading}
        />

        {!shouldSkipPayment && (
          <>
            <PaymentOptionsToggle
              control={form.control}
              isLoading={isLoading}
              watchPaymentOption={watchPaymentOption}
            />

            <PaymentLinkField
              control={form.control}
              isLoading={isLoading}
              isVisible={watchPaymentOption === 'manual'}
            />
          </>
        )}

        <QuoteIdentifierField
          form={form}
          isVisible={isAdmin}
        />

        <Button type="submit" disabled={isLoading} className="w-full">
          {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {isSubmitting ? "Adding Quote..." : uploading ? "Uploading..." : "Add Quote"}
        </Button>
      </form>
    </Form>
  );
};

export default QuoteForm;
